import type { AdminV2MediaMetaContext } from './media-meta-context';
import { readAdminV2MediaMetaValue } from './media-meta-context';
import type { AdminV2ToolDraft } from './tool-contract';
import { resolveAdminV2RegisteredCreditCalculator } from './tool-runtime-registries';

type CreditFactor =
  AdminV2ToolDraft['userCreditPolicy']['terms'][number]['factors'][number];

export type AdminV2ToolCreditFactorEvidence = {
  factorIndex: number;
  kind: CreditFactor['kind'];
  credits: number;
  source: string;
  value?: unknown;
  matchedValue?: unknown;
  defaultUsed?: boolean;
};

export type AdminV2ToolCreditTermEvidence = {
  termIndex: number;
  credits: number;
  factors: AdminV2ToolCreditFactorEvidence[];
};

export type AdminV2ToolCreditEvidence = {
  totalCredits: number;
  terms: AdminV2ToolCreditTermEvidence[];
  paidAccessDiscount?: {
    percentOff: number;
    applied: boolean;
    creditsBeforeDiscount: number;
  };
};

function assertNonNegativeInteger(value: number, label: string) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${label} must be a non-negative integer.`);
  }
  return value;
}

function countOutputs(value: unknown): number {
  if (Array.isArray(value)) {
    return value.reduce((sum, item) => sum + countOutputs(item), 0);
  }
  if (value === undefined || value === null || value === '') {
    return 0;
  }
  return 1;
}

function toNonNegativeInteger(value: unknown, label: string) {
  if (typeof value === 'number') {
    return assertNonNegativeInteger(value, label);
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return assertNonNegativeInteger(parsed, label);
  }
  throw new Error(`${label} must be a non-negative integer.`);
}

function matchesConditionalCreditFactor(params: {
  factor: Extract<CreditFactor, { kind: 'conditional' }>;
  input: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
}) {
  return params.factor.conditions.every((condition) => {
    const value =
      condition.source === 'toolField'
        ? params.input[condition.fieldId]
        : readAdminV2MediaMetaValue({
            context: params.mediaMetaContext,
            fieldId: condition.fieldId,
            metaSource: 'clientProvidedMeta',
            metaPath: condition.metaPath,
          });
    return Object.is(value, condition.equals);
  });
}

function getFactorEvidence(params: {
  factor: CreditFactor;
  factorIndex: number;
  input: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  outputs?: Record<string, unknown>;
  executedModelUsageIds: string[];
  executedNodeIds: string[];
}): AdminV2ToolCreditFactorEvidence {
  if (params.factor.kind === 'constant') {
    return {
      factorIndex: params.factorIndex,
      kind: params.factor.kind,
      credits: assertNonNegativeInteger(
        params.factor.value,
        'Constant credit factor'
      ),
      source: 'constant',
      value: params.factor.value,
    };
  }

  if (params.factor.kind === 'inputCount') {
    const value = params.input[params.factor.fieldId];
    if (Array.isArray(value)) {
      return {
        factorIndex: params.factorIndex,
        kind: params.factor.kind,
        credits: value.length,
        source: 'input',
        value,
      };
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      return {
        factorIndex: params.factorIndex,
        kind: params.factor.kind,
        credits: 1,
        source: 'input',
        value,
      };
    }
    return {
      factorIndex: params.factorIndex,
      kind: params.factor.kind,
      credits: assertNonNegativeInteger(
        params.factor.defaultValue,
        `Credit image count default ${params.factor.fieldId}`
      ),
      source: 'default',
      defaultUsed: true,
    };
  }

  if (params.factor.kind === 'toolField') {
    const value = params.input[params.factor.fieldId];
    const optionValue = params.factor.values.find((candidate) =>
      Object.is(candidate.value, value)
    );
    if (optionValue) {
      return {
        factorIndex: params.factorIndex,
        kind: params.factor.kind,
        credits: assertNonNegativeInteger(
          optionValue.credits,
          `Credit field ${params.factor.fieldId}`
        ),
        source: 'input-option',
        value,
        matchedValue: optionValue.value,
      };
    }
    if (
      params.factor.values.length > 0 ||
      value === undefined ||
      value === null ||
      value === ''
    ) {
      return {
        factorIndex: params.factorIndex,
        kind: params.factor.kind,
        credits: assertNonNegativeInteger(
          params.factor.defaultValue,
          `Credit field default ${params.factor.fieldId}`
        ),
        source: 'default',
        value,
        defaultUsed: true,
      };
    }
    if (typeof value === 'number') {
      return {
        factorIndex: params.factorIndex,
        kind: params.factor.kind,
        credits: assertNonNegativeInteger(
          value,
          `Credit factor field ${params.factor.fieldId}`
        ),
        source: 'input-number',
        value,
      };
    }
    throw new Error(
      `Credit factor field ${params.factor.fieldId} must be a non-negative integer.`
    );
  }

  if (params.factor.kind === 'conditional') {
    const matched = matchesConditionalCreditFactor({
      factor: params.factor,
      input: params.input,
      mediaMetaContext: params.mediaMetaContext,
    });
    return {
      factorIndex: params.factorIndex,
      kind: params.factor.kind,
      credits: matched
        ? assertNonNegativeInteger(
            params.factor.credits,
            'Conditional credit factor'
          )
        : assertNonNegativeInteger(
            params.factor.defaultValue,
            'Conditional credit factor default'
          ),
      source: matched ? 'conditional-match' : 'conditional-default',
      matchedValue: matched ? params.factor.conditions : undefined,
      defaultUsed: !matched,
    };
  }

  if (params.factor.kind === 'executionBranch') {
    const branch = params.factor.branches.find((candidate) =>
      params.executedModelUsageIds.includes(candidate.modelUsageId)
    );
    return {
      factorIndex: params.factorIndex,
      kind: params.factor.kind,
      credits: assertNonNegativeInteger(
        branch?.credits ?? params.factor.defaultValue,
        'Credit execution branch'
      ),
      source: branch ? 'executed-model-usage' : 'default',
      matchedValue: branch?.modelUsageId,
      defaultUsed: !branch,
    };
  }

  if (params.factor.kind === 'outputCount') {
    const outputCount = countOutputs(
      params.factor.outputKey
        ? params.outputs?.[params.factor.outputKey]
        : params.outputs
    );
    return {
      factorIndex: params.factorIndex,
      kind: params.factor.kind,
      credits: outputCount || params.factor.defaultValue,
      source: outputCount > 0 ? 'outputs' : 'default',
      value: outputCount,
      defaultUsed: outputCount === 0,
    };
  }

  if (params.factor.kind === 'mediaMeta') {
    const value = readAdminV2MediaMetaValue({
      context: params.mediaMetaContext,
      fieldId: params.factor.fieldId,
      metaSource: params.factor.metaSource,
      metaPath: params.factor.metaPath,
    });
    if (value === undefined || value === null || value === '') {
      return {
        factorIndex: params.factorIndex,
        kind: params.factor.kind,
        credits: params.factor.defaultValue,
        source: 'default',
        defaultUsed: true,
      };
    }
    return {
      factorIndex: params.factorIndex,
      kind: params.factor.kind,
      credits: toNonNegativeInteger(value, 'Credit media metadata factor'),
      source: 'media-meta',
      value,
    };
  }

  if (params.factor.kind === 'nodeExecuted') {
    const executed = params.executedNodeIds.includes(params.factor.nodeId);
    return {
      factorIndex: params.factorIndex,
      kind: params.factor.kind,
      credits: executed ? params.factor.credits : params.factor.defaultValue,
      source: executed ? 'executed-node' : 'default',
      matchedValue: executed ? params.factor.nodeId : undefined,
      defaultUsed: !executed,
    };
  }

  const registeredValue = resolveAdminV2RegisteredCreditCalculator({
    key: params.factor.key,
    normalizedInput: params.input,
    mediaMetaContext: params.mediaMetaContext,
    outputs: params.outputs,
    executedModelUsageIds: params.executedModelUsageIds,
    executedNodeIds: params.executedNodeIds,
  });
  return {
    factorIndex: params.factorIndex,
    kind: params.factor.kind,
    credits: assertNonNegativeInteger(
      registeredValue,
      `Credit calculator ${params.factor.key}`
    ),
    source: 'registered-calculator',
    matchedValue: params.factor.key,
  };
}

function calculateCreditTerms(params: {
  terms: AdminV2ToolDraft['userCreditPolicy']['terms'];
  input: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
  outputs?: Record<string, unknown>;
  executedModelUsageIds: string[];
  executedNodeIds: string[];
}) {
  return params.terms.map((term, termIndex) => {
    const factors = term.factors.map((factor, factorIndex) =>
      getFactorEvidence({
        factor,
        factorIndex,
        input: params.input,
        mediaMetaContext: params.mediaMetaContext,
        outputs: params.outputs,
        executedModelUsageIds: params.executedModelUsageIds,
        executedNodeIds: params.executedNodeIds,
      })
    );
    const credits = factors.reduce(
      (product, factor) => product * factor.credits,
      1
    );
    return {
      termIndex,
      credits: assertNonNegativeInteger(credits, 'Credit term total'),
      factors,
    };
  });
}

function sumCreditTerms(terms: AdminV2ToolCreditTermEvidence[]) {
  return terms.reduce(
    (sum, term) => assertNonNegativeInteger(sum + term.credits, 'Credit total'),
    0
  );
}

export function calculateAdminV2ToolCreditEvidence(params: {
  policy: AdminV2ToolDraft['userCreditPolicy'];
  input: Record<string, unknown>;
  hasPaidAccess?: boolean;
  mediaMetaContext?: AdminV2MediaMetaContext;
  outputs?: Record<string, unknown>;
  executedModelUsageIds?: string[];
  executedNodeIds?: string[];
}): AdminV2ToolCreditEvidence {
  const executedModelUsageIds = params.executedModelUsageIds ?? [];
  const executedNodeIds = params.executedNodeIds ?? [];
  const terms = calculateCreditTerms({
    terms: params.policy.terms,
    input: params.input,
    mediaMetaContext: params.mediaMetaContext,
    outputs: params.outputs,
    executedModelUsageIds,
    executedNodeIds,
  });

  const creditsBeforeDiscount = sumCreditTerms(terms);
  const discount = params.policy.paidAccessDiscount;
  if (!discount) {
    return { totalCredits: creditsBeforeDiscount, terms };
  }

  if (
    !Number.isSafeInteger(discount.percentOff) ||
    discount.percentOff < 1 ||
    discount.percentOff > 100
  ) {
    throw new Error(
      'Paid access discount percent must be an integer from 1 to 100.'
    );
  }
  const applied = params.hasPaidAccess === true;
  const discountedCredits = discount.terms
    ? sumCreditTerms(
        calculateCreditTerms({
          terms: discount.terms,
          input: params.input,
          mediaMetaContext: params.mediaMetaContext,
          outputs: params.outputs,
          executedModelUsageIds,
          executedNodeIds,
        })
      )
    : (() => {
        const discountedNumerator = assertNonNegativeInteger(
          creditsBeforeDiscount * (100 - discount.percentOff),
          'Discounted credit numerator'
        );
        return assertNonNegativeInteger(
          discountedNumerator / 100,
          'Discounted credit total'
        );
      })();
  const totalCredits = applied ? discountedCredits : creditsBeforeDiscount;

  return {
    totalCredits,
    terms,
    paidAccessDiscount: {
      percentOff: discount.percentOff,
      applied,
      creditsBeforeDiscount,
    },
  };
}

export function calculateAdminV2ToolCredits(params: {
  policy: AdminV2ToolDraft['userCreditPolicy'];
  input: Record<string, unknown>;
  hasPaidAccess?: boolean;
  mediaMetaContext?: AdminV2MediaMetaContext;
  outputs?: Record<string, unknown>;
  executedModelUsageIds?: string[];
  executedNodeIds?: string[];
}) {
  return calculateAdminV2ToolCreditEvidence(params).totalCredits;
}
